import { useEffect } from 'react';

import { isFunction as _isFunction } from 'lodash';

import ContentManager from '@vfgroup-oneplatform/foundation/ContentManager';
import { usePrevious } from '@vfgroup-oneplatform/foundation/Utils/Hooks';

import { PaymentTrackingManager } from '../../Utils/TrackingManagers';
import { PaymentScreenTypes, PaymentTypes } from '../Utils/PaymentTypes';

const { DEFAULT, PAYMENTREQUEST, PAYMENTSUCCESSFULLY, PAYMENTFAILD, ADD_PAYMENT_CARD } =
  PaymentScreenTypes;

const { GIFT_AFTER_PAY, BILLING_PAYMENT } = PaymentTypes;

const trackBillingPayment = (
  paymentOptions,
  prevPaymentOptions,
  status,
  prevPaymentStatus,
  singleValue,
  additionalData,
) => {
  switch (status) {
    case DEFAULT:
      if (!paymentOptions.screen && !prevPaymentStatus) {
        // inital bill payment view
        PaymentTrackingManager.trackView('QA / Pay bill', BILLING_PAYMENT, {
          visitor_amount_current_active: singleValue.value,
          ...additionalData,
        });
      }
      // on Done press after successful payment
      else if (prevPaymentStatus === PAYMENTSUCCESSFULLY) {
        PaymentTrackingManager.trackEvent('ui_interaction', BILLING_PAYMENT, {
          event_action: 'OnClick',
          event_category: 'Button',
          event_label: `${ContentManager.translate(
            'pay_bill_quick_action_successful_button_title',
          )}`,
          visitor_amount_current_active: singleValue.value,
          ...additionalData,
        });
      }
      // on Cancel press and closing QA
      else if (!prevPaymentOptions.isPaymentDismissed && paymentOptions.isPaymentDismissed) {
        PaymentTrackingManager.trackEvent('ui_interaction', BILLING_PAYMENT, {
          event_action: 'OnClick',
          event_category: 'Button',
          event_label: `${ContentManager.translate('pay_bill_quick_action_close_button_text')}`,
          visitor_amount_current_active: singleValue.value,
          ...additionalData,
        });
      }
      break;
    // loading screen
    case PAYMENTREQUEST:
      if (!paymentOptions.screen) {
        //loading screen from default screen
        if (prevPaymentStatus === DEFAULT) {
          PaymentTrackingManager.trackEvent('ui_interaction', BILLING_PAYMENT, {
            event_action: 'OnClick',
            event_category: 'Button',
            event_label: `${ContentManager.translate('payment_quick_action_confirm_button_text')}`,
            visitor_amount_current_active: singleValue.value,
            ...additionalData,
          });
        }
        //loading screen from error screen
        if (!paymentOptions.screen && prevPaymentStatus === PAYMENTFAILD) {
          PaymentTrackingManager.trackEvent('ui_interaction', BILLING_PAYMENT, {
            event_action: 'OnClick',
            event_category: 'Button',
            event_label: `${ContentManager.translate('pay_bill_quick_action_failure_try_again')}`,
            visitor_amount_current_active: singleValue.value,
            ...additionalData,
          });
        }
        PaymentTrackingManager.trackView('QA / Loading status', BILLING_PAYMENT, additionalData);
      }
      break;
    //success screen
    case PAYMENTSUCCESSFULLY:
      if (prevPaymentStatus === PAYMENTREQUEST) {
        PaymentTrackingManager.trackView('QA / Success', BILLING_PAYMENT, {
          visitor_amount_current_active: singleValue.value,
          transaction_status: 'Paid',
          ...additionalData,
        });
      }
      break;
    //error screen
    case PAYMENTFAILD:
      if (prevPaymentStatus !== PAYMENTFAILD) {
        PaymentTrackingManager.trackView('QA / Error', BILLING_PAYMENT, {
          page_error: 'Something went wrong!',
          ...additionalData,
        });
      }
      break;
  }
};

function usePaymentTracking(props) {
  const {
    status,
    type,
    selectedValue,
    paymentOptions,
    getAdditionalTrackingInfo,
    value: singleValue,
  } = props;
  const prevPaymentStatus = usePrevious(status);
  const prevPaymentOptions = usePrevious(paymentOptions);

  const trackStatusChange = commonTrackingInfo => {
    if (status === DEFAULT) {
      // default topup screen
      PaymentTrackingManager.trackView(
        'page_view',
        GIFT_AFTER_PAY,
        getAdditionalTrackingInfo(commonTrackingInfo),
      );
    }
    if (status === PAYMENTREQUEST) {
      // on payment loading
      PaymentTrackingManager.trackView(
        'page_view',
        GIFT_AFTER_PAY,
        getAdditionalTrackingInfo({
          ...commonTrackingInfo,
          page_section: 'Dashboard:Top-up:QA:Loading',
        }),
      );
    }
    if (status === PAYMENTSUCCESSFULLY) {
      // on payment success
      PaymentTrackingManager.trackView(
        'page_view',
        GIFT_AFTER_PAY,
        getAdditionalTrackingInfo({
          ...commonTrackingInfo,
          page_section: 'Dashboard:Top-up:QA:Success',
        }),
      );
    }
    if (status === PAYMENTFAILD) {
      // on payment failed
      PaymentTrackingManager.trackView(
        'page_view',
        GIFT_AFTER_PAY,
        getAdditionalTrackingInfo({
          ...commonTrackingInfo,
          page_section: 'Dashboard:Top-up:QA:Error',
        }),
      );
    }
  };

  useEffect(() => {
    if (type === GIFT_AFTER_PAY) {
      const commonTrackingInfo = {
        product_name: 'Top-Up',
        product_price: selectedValue && selectedValue?.value,
      };
      trackStatusChange(commonTrackingInfo);

      if (paymentOptions.isCardListOpend && !paymentOptions.screen) {
        // on EditPayment tracking Event BTN pressed,
        PaymentTrackingManager.trackEvent(
          'ui_interaction',
          GIFT_AFTER_PAY,
          getAdditionalTrackingInfo({
            ...commonTrackingInfo,
            event_action: 'OnClick',
            event_category: 'Button',
            event_label: `${ContentManager.translate('quick_action_payment_method_edit')}`,
          }),
        );
        // view tracked EditPayment
        PaymentTrackingManager.trackView(
          'page_view',
          GIFT_AFTER_PAY,
          getAdditionalTrackingInfo({
            ...commonTrackingInfo,
            page_section: 'Top-up:QA:Edit-payment',
          }),
        );
        // view tracked Add-new-PM
        // "Add new" button appeared
        PaymentTrackingManager.trackView(
          'page_view',
          GIFT_AFTER_PAY,
          getAdditionalTrackingInfo({
            ...commonTrackingInfo,
            page_section: 'Top-up:QA:Add-new-PM',
          }),
        );
      }
      if (paymentOptions.screen === ADD_PAYMENT_CARD) {
        // onAddCard tracking Event BTN pressed,
        PaymentTrackingManager.trackEvent(
          'ui_interaction',
          GIFT_AFTER_PAY,
          getAdditionalTrackingInfo({
            ...commonTrackingInfo,
            event_action: 'OnClick',
            event_category: 'Button',
            event_label: `${ContentManager.translate(
              'top_up_quick_action_add_payment_method_item_text',
            )}`,
          }),
        );
        // view tracked Add-new-payment
        PaymentTrackingManager.trackView(
          'page_view',
          GIFT_AFTER_PAY,
          getAdditionalTrackingInfo({
            ...commonTrackingInfo,
            page_section: 'Top-up:QA:Add-new-payment',
          }),
        );

        // form-start event tracked
        PaymentTrackingManager.trackView(
          'form_start',
          GIFT_AFTER_PAY,
          getAdditionalTrackingInfo({
            ...commonTrackingInfo,
            page_section: 'Top-up:QA:Add-new-payment',
            form_name: 'Add new payment method',
            form_step_name: 'Add new credit / debit card',
            form_type: 'payment',
          }),
        );
      }
      if (paymentOptions.isSaveNewCard && status != PAYMENTREQUEST) {
        // form-end event tracked
        PaymentTrackingManager.trackView(
          'form_end',
          GIFT_AFTER_PAY,
          getAdditionalTrackingInfo({
            ...commonTrackingInfo,
            page_section: 'Top-up:QA:Add-new-payment',
            form_name: 'Add new payment method',
            form_step_name: 'Add new credit / debit card',
            form_type: 'payment',
          }),
        );
        // onSaveNewCard event tracked
        PaymentTrackingManager.trackEvent(
          'ui_interaction',
          GIFT_AFTER_PAY,
          getAdditionalTrackingInfo({
            ...commonTrackingInfo,
            page_section: 'Top-up:QA:Add-new-payment',
            event_action: 'OnClick',
            event_category: 'Button',
            event_label: `${ContentManager.translate('quick_action_add_new_payment_card_done')}`,
          }),
        );
      }
      if (paymentOptions?.isConfirmPayment) {
        // onConfirmPayment event tracked
        PaymentTrackingManager.trackEvent(
          'ui_interaction',
          GIFT_AFTER_PAY,
          getAdditionalTrackingInfo({
            ...commonTrackingInfo,
            event_action: 'OnClick',
            event_category: 'Button',
            event_label: `${ContentManager.translate('top_up_quick_action_confirm_button_text')}`,
          }),
        );
      }
    } else if (type === BILLING_PAYMENT) {
      const additionalData = _isFunction(getAdditionalTrackingInfo) && getAdditionalTrackingInfo();

      trackBillingPayment(
        paymentOptions,
        prevPaymentOptions,
        status,
        prevPaymentStatus,
        singleValue,
        additionalData,
      );
    }
  }, [status, type, selectedValue, paymentOptions, singleValue]);
}
export default usePaymentTracking;
export { trackBillingPayment };
